import { FunctionComponent, useState } from 'react';
import {
    Autocomplete,
    Box,
    Button,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { DrawerActions } from '@starjack/shared-components';
import { LoadingButton } from '@mui/lab';
import { useNotification } from '@starjack/features/notifications';
import {
    CreateCoupon,
    getNUmberFromContingent,
    isCreateCouponComplete,
} from '@/models/coupon';
import { usePostCouponsMutation } from '@/api/v2/coupon';
import { useGetAllLiftOperatorsQuery } from '@/api/v2/liftOperator';
import { LiftOperator } from '@/models/liftoperator';
import { DatePicker } from '@/components/DatePicker/DatePicker';
import { NumberInput } from '@/components/NumberInput/NumberInput';
import { MoneyInput } from '@/components/MoneyInput/MoneyInput';
import { isBefore } from 'date-fns';
import isEqual from 'lodash/isEqual';
import { useDirtyGuard } from '@starjack/shared-components';

const getSortedLiftOperators = (liftOperators?: LiftOperator[]) => {
    return (
        liftOperators
            ?.slice()
            .sort((a, b) => a.name.de.localeCompare(b.name.de)) || []
    );
};

type CouponDrawerContentProps = {
    coupon: CreateCoupon;
    onClose: () => void;
};

export const CreateCouponDrawerContent: FunctionComponent<
    CouponDrawerContentProps
> = ({ coupon, onClose }) => {
    const [localCoupon, setLocalCoupon] = useState<CreateCoupon>(coupon);
    useDirtyGuard(!isEqual(localCoupon, coupon));
    const { addNotification } = useNotification();
    const [postCoupon, { isLoading }] = usePostCouponsMutation();
    const [validToError, setValidToError] = useState(false);
    const { data: liftOperators } = useGetAllLiftOperatorsQuery();

    const onChangeHandler = (key: keyof CreateCoupon) => (e) => {
        const { value } = e.target;

        setLocalCoupon((prevLocalCoupon) => ({
            ...prevLocalCoupon,
            [key]: value,
        }));
    };

    const onCreateCoupon = () => {
        postCoupon(localCoupon as CreateCoupon)
            .unwrap()
            .then(() => {
                addNotification({
                    message: 'Speichern erfolgreich',
                    type: 'success',
                });

                onClose();
            })
            .catch((reason) => {
                console.error(reason);

                addNotification({
                    message:
                        'Fehler beim Speichern. Bitte später noch einmal versuchen',
                    type: 'error',
                });
            });
    };

    if (!localCoupon) return null;

    return (
        <Box
            sx={{
                width: '480px',
            }}
        >
            <Box
                sx={(theme) => ({
                    backgroundColor: theme.palette.primary.dark,
                    color: '#fff',
                    p: 4,
                })}
            >
                <Typography variant='h6'>Neuen Coupon erstellen</Typography>
            </Box>

            <Stack
                sx={{
                    p: 4,
                }}
                spacing={3}
            >
                <TextField
                    value={localCoupon.name}
                    onChange={onChangeHandler('name')}
                    label='Name'
                    inputProps={{ maxLength: 99 }}
                />

                <NumberInput
                    onChange={(nextValue) => {
                        setLocalCoupon((prevState) => ({
                            ...prevState,
                            contingent: nextValue?.toString() || '',
                        }));
                    }}
                    value={
                        getNUmberFromContingent(localCoupon.contingent) ?? null
                    }
                    label='Kontingent (gilt pro Code)'
                />

                <MoneyInput
                    onChange={(nextValue) => {
                        setLocalCoupon((prevState) => ({
                            ...prevState,
                            amount: {
                                amount: nextValue?.amount,
                                currency: 'EUR',
                            },
                        }));
                    }}
                    value={localCoupon.amount || null}
                    label='Betrag'
                />

                <DatePicker
                    onChange={(nextDate) => {
                        if (!nextDate && validToError) {
                            setValidToError(false);
                        }

                        setLocalCoupon((prevState) => ({
                            ...prevState,
                            validFrom: nextDate,
                        }));
                    }}
                    value={localCoupon.validFrom}
                    label='Gültig ab'
                    disablePast
                />

                <DatePicker
                    onChange={(nextDate) => {
                        if (localCoupon.validFrom) {
                            if (isBefore(nextDate, localCoupon.validFrom)) {
                                setValidToError(true);
                                return;
                            }
                        }

                        if (validToError) {
                            setValidToError(false);
                        }

                        setLocalCoupon((prevState) => ({
                            ...prevState,
                            validUntil: nextDate,
                        }));
                    }}
                    value={localCoupon.validUntil}
                    label='Gültig bis'
                    error={validToError}
                    helperText={
                        validToError
                            ? 'Das Enddatum muss nach dem Startdatum (falls gesetzt) liegen'
                            : ''
                    }
                    disablePast
                />

                <Autocomplete
                    options={getSortedLiftOperators(liftOperators)}
                    getOptionLabel={(option: LiftOperator) => option.name.de}
                    getOptionKey={(option: LiftOperator) => option.id}
                    value={
                        liftOperators?.find(
                            (op) => op.id === localCoupon.liftOperatorId
                        ) || null
                    }
                    onChange={(_, value) => {
                        setLocalCoupon((prevState) => ({
                            ...prevState,
                            liftOperatorId: value?.id || undefined,
                        }));
                    }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label='Bergbahn (optional)'
                            helperText='Kann nach Erstellung nicht mehr geändert werden'
                        />
                    )}
                />

                <TextField
                    label='Codes'
                    onChange={(e) => {
                        const { value } = e.target;

                        setLocalCoupon((prevState) => ({
                            ...prevState,
                            codes: value,
                        }));
                    }}
                    rows={3}
                    multiline
                />

                <DrawerActions>
                    <Stack
                        sx={{
                            alignItems: 'center',
                        }}
                        spacing={2}
                        direction='row'
                    >
                        <Button variant='outlined' onClick={onClose}>
                            Abbrechen
                        </Button>

                        <LoadingButton
                            variant='contained'
                            onClick={onCreateCoupon}
                            disabled={
                                !isCreateCouponComplete(localCoupon) ||
                                validToError
                            }
                            loading={isLoading}
                        >
                            Speichern
                        </LoadingButton>
                    </Stack>
                </DrawerActions>
            </Stack>
        </Box>
    );
};
